import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CheckCircle, FileText, EnvelopeSimple, Warning, Plus, X, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULT_SUBJECT = 'Πρακτικά προς Έγκριση – {{building_name}}';
const DEFAULT_BODY = `Αγαπητέ/ή {{manager_name}},

Σας αποστέλλονται τα πρακτικά της συνέλευσης για έγκριση.

📅 Ημερομηνία: {{meeting_date}}
📍 Κτίριο: {{building_name}}

Παρακαλώ επιλέξτε μία από τις παρακάτω ενέργειες:

  ✅ APPROVE: [Approve]
  ❌ REJECT:  [Reject]
  ✏️  REQUEST CHANGES: [Request Changes]

Ευχαριστώ`;

/**
 * Phase 207m / 735.6 — Approval Email Composition Modal.
 *
 * Sender can now mix three recipient sources:
 *   • Active Management Committee (auto-filled, non-removable — the original
 *     approval workflow the app was built around).
 *   • Additional apartment owners (searchable picker with add / remove).
 *   • Custom free-form email addresses (accountant, external counsel, etc.).
 *
 * Send is enabled as long as at least one recipient has an email address.
 */
export default function ApprovalEmailDialog({ open, onOpenChange, buildingId, meeting, onSubmitted }) {
  const [committee, setCommittee] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [selectedApts, setSelectedApts] = useState([]); // [apartment_id]
  const [customEmails, setCustomEmails] = useState([]); // [{email, name}]
  const [newEmail, setNewEmail] = useState('');
  const [aptSearch, setAptSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);

  useEffect(() => {
    if (!open || !buildingId) return;
    setLoading(true);
    setSubject(DEFAULT_SUBJECT);
    setBody(DEFAULT_BODY);
    setSelectedApts([]);
    setCustomEmails([]);
    setNewEmail('');
    setAptSearch('');
    Promise.all([
      axios.get(`${API}/buildings/${buildingId}/active-committee`).catch(() => ({ data: { members: [] } })),
      axios.get(`${API}/buildings/${buildingId}/apartments`).catch(() => ({ data: [] })),
    ])
      .then(([cRes, aRes]) => {
        setCommittee(cRes.data?.members || []);
        setApartments(Array.isArray(aRes.data) ? aRes.data : []);
      })
      .finally(() => setLoading(false));
  }, [open, buildingId]);

  const committeeApartmentIds = useMemo(
    () => new Set(committee.map((m) => m.apartment_id).filter(Boolean)),
    [committee]
  );

  const availableApartments = useMemo(() => {
    const q = aptSearch.trim().toLowerCase();
    return apartments
      .filter((a) => !committeeApartmentIds.has(a.apartment_id))
      .filter((a) => !selectedApts.includes(a.apartment_id))
      .filter((a) => {
        if (!q) return true;
        return (
          String(a.apartment_number || '').toLowerCase().includes(q) ||
          (a.owner_name || '').toLowerCase().includes(q) ||
          (a.email || '').toLowerCase().includes(q)
        );
      })
      .slice(0, 8);
  }, [apartments, aptSearch, selectedApts, committeeApartmentIds]);

  const selectedApartmentObjs = useMemo(
    () => selectedApts.map((id) => apartments.find((a) => a.apartment_id === id)).filter(Boolean),
    [selectedApts, apartments]
  );

  const committeeEmailCount = useMemo(
    () => committee.filter((r) => (r.email || '').trim()).length,
    [committee]
  );
  const extraApartmentEmailCount = selectedApartmentObjs.filter((a) => (a.email || '').trim()).length;
  const customEmailCount = customEmails.length;
  const totalEmailRecipients = committeeEmailCount + extraApartmentEmailCount + customEmailCount;
  const hasEmails = totalEmailRecipients > 0;

  const addCustomEmail = () => {
    const e = newEmail.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error('Μη έγκυρο email');
      return;
    }
    if (customEmails.some((x) => x.email.toLowerCase() === e.toLowerCase())) {
      toast.error('Το email υπάρχει ήδη');
      return;
    }
    setCustomEmails([...customEmails, { email: e, name: e }]);
    setNewEmail('');
  };

  const removeCustomEmail = (email) =>
    setCustomEmails(customEmails.filter((x) => x.email !== email));

  const removeApartment = (id) =>
    setSelectedApts(selectedApts.filter((x) => x !== id));

  const interp = (s) =>
    (s || '')
      .replace(/\{\{building_name\}\}/g, meeting?.building_name || '')
      .replace(/\{\{meeting_date\}\}/g, meeting?.date || '');

  const subjectPreview = interp(subject);
  const bodyPreview = interp(body);

  const handleSend = async () => {
    if (!hasEmails) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/buildings/${buildingId}/meetings/${meeting.meeting_id}/submit-for-approval`,
        {
          subject,
          body,
          additional_apartments: selectedApts,
          additional_emails: customEmails,
        },
      );
      // Phase 735.8 — Show the REAL delivery result (sent / failed / mocked)
      // instead of the previous silent "success".
      const dc = res.data?.delivery_counts || {};
      const dm = res.data?.delivery_mode || 'mocked';
      const errs = res.data?.delivery_errors || [];
      if (dm === 'sent') {
        toast.success(`Ο απεσταλμένος αριθμός emails: ${dc.sent}. ${dc.failed ? `Αποτυχία: ${dc.failed}` : ''}`);
      } else if (dm === 'failed') {
        const first = errs[0];
        toast.error(
          `Αποτυχία αποστολής (${dc.failed}). ${first?.error_message || 'Ελέγξτε το κλειδί Brevo στις ρυθμίσεις.'}`,
          { duration: 8000 }
        );
      } else {
        toast.warning(
          `Το κλειδί Brevo δεν έχει ρυθμιστεί — τα emails καταγράφηκαν ως mock (${dc.mocked}).`,
          { duration: 8000 }
        );
      }
      onSubmitted && onSubmitted(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code === 'no_active_committee') {
        toast.error('Δεν υπάρχουν παραλήπτες. Προσθέστε επιτροπή ή επιπλέον emails.');
      } else {
        toast.error(typeof detail === 'string' ? detail : (err?.message || 'Σφάλμα'));
      }
    } finally {
      setSending(false);
    }
  };

  const ROLE_LABELS = {
    president: 'Πρόεδρος', chairman: 'Πρόεδρος',
    treasurer: 'Ταμίας', secretary: 'Γραμματέας',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="approval-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EnvelopeSimple size={18} weight="fill" className="text-amber-600" />
            Αποστολή για Έγκριση
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-zinc-400 text-sm">Φόρτωση…</div>
        ) : (
          <div className="space-y-5">
            {/* --- 1. Committee (auto) --- */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Διαχειριστική Επιτροπή
                <span className="text-zinc-400 font-normal normal-case ml-1">(αυτόματα)</span>
              </Label>
              {committee.length === 0 ? (
                <div className="mt-1 px-3 py-2.5 border border-zinc-200 bg-zinc-50 text-xs text-zinc-500 italic">
                  Δεν υπάρχουν active μέλη επιτροπής.
                </div>
              ) : (
                <div className="mt-1 border border-zinc-200 divide-y divide-zinc-100" data-testid="approval-recipients">
                  {committee.map((r, i) => {
                    const roleLabel = ROLE_LABELS[(r.role || '').toLowerCase()] || (r.role || 'Μέλος');
                    const hasEmail = !!(r.email || '').trim();
                    return (
                      <div key={i} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`approval-recipient-${i}`}>
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 w-20 flex-shrink-0">{roleLabel}</span>
                        <span className="font-medium flex-1 truncate">{r.name || '—'}</span>
                        {hasEmail ? (
                          <span className="font-mono text-xs text-zinc-600 truncate">{r.email}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700"><Warning size={12} weight="fill" />χωρίς email</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* --- 2. Additional apartments --- */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Επιπλέον Ιδιοκτήτες
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-zinc-400 flex-shrink-0" />
                <Input
                  value={aptSearch}
                  onChange={(e) => setAptSearch(e.target.value)}
                  placeholder="Αναζήτηση με αριθμό διαμερίσματος ή όνομα..."
                  className="rounded-none text-sm h-8"
                  data-testid="approval-apt-search"
                />
              </div>
              {aptSearch.trim() && availableApartments.length > 0 && (
                <div className="mt-1 border border-zinc-200 max-h-40 overflow-y-auto" data-testid="approval-apt-results">
                  {availableApartments.map((a) => (
                    <button
                      key={a.apartment_id}
                      type="button"
                      onClick={() => { setSelectedApts([...selectedApts, a.apartment_id]); setAptSearch(''); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 flex items-center gap-2"
                      data-testid={`approval-apt-add-${a.apartment_id}`}
                    >
                      <span className="font-mono text-xs text-zinc-500 w-14">{a.apartment_number}</span>
                      <span className="flex-1 truncate">{a.owner_name || '—'}</span>
                      {a.email ? (
                        <span className="text-xs text-zinc-500 truncate">{a.email}</span>
                      ) : (
                        <span className="text-[10px] text-amber-700">χωρίς email</span>
                      )}
                      <Plus size={12} className="text-emerald-600 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
              {selectedApartmentObjs.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="approval-apt-chips">
                  {selectedApartmentObjs.map((a) => (
                    <span key={a.apartment_id} className="inline-flex items-center gap-1 px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-xs" data-testid={`approval-apt-chip-${a.apartment_id}`}>
                      <span className="font-mono text-zinc-500">{a.apartment_number}</span>
                      <span>{a.owner_name || '—'}</span>
                      {!a.email && <Warning size={10} className="text-amber-600" weight="fill" />}
                      <button type="button" onClick={() => removeApartment(a.apartment_id)} className="hover:text-red-600" data-testid={`approval-apt-remove-${a.apartment_id}`}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* --- 3. Custom emails --- */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                Προσαρμοσμένα Emails
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomEmail(); } }}
                  placeholder="π.χ. accountant@company.com"
                  className="rounded-none text-sm h-8"
                  data-testid="approval-custom-email-input"
                />
                <Button type="button" size="sm" variant="outline" className="rounded-none h-8" onClick={addCustomEmail} data-testid="approval-custom-email-add">
                  <Plus size={14} className="mr-1" />Add Email
                </Button>
              </div>
              {customEmails.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="approval-custom-email-chips">
                  {customEmails.map((c) => (
                    <span key={c.email} className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-200 bg-blue-50 text-xs" data-testid={`approval-custom-email-chip-${c.email}`}>
                      <span className="font-mono text-blue-700">{c.email}</span>
                      <button type="button" onClick={() => removeCustomEmail(c.email)} className="hover:text-red-600">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <p className="text-[11px] text-zinc-500" data-testid="approval-total-summary">
              Σύνολο παραληπτών με email: <b>{totalEmailRecipients}</b>
              {' '}({committeeEmailCount} επιτροπή, {extraApartmentEmailCount} επιπλέον ιδιοκτήτες, {customEmailCount} custom)
            </p>

            {/* Subject */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Θέμα (Subject)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 rounded-none text-sm" data-testid="approval-subject-input" />
              {subjectPreview !== subject && <p className="text-[10px] text-zinc-400 mt-1 italic font-mono">→ {subjectPreview}</p>}
            </div>

            {/* Body */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Email Body</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="mt-1 rounded-none text-sm font-mono" data-testid="approval-body-input" />
              <p className="text-[10px] text-zinc-400 mt-1">
                Placeholders: <code className="bg-zinc-100 px-1">{'{{building_name}}'}</code>{' '}
                <code className="bg-zinc-100 px-1">{'{{meeting_date}}'}</code>{' '}
                <code className="bg-zinc-100 px-1">{'{{manager_name}}'}</code>
              </p>
            </div>

            <div className="px-3 py-2 bg-blue-50/50 border border-blue-100 flex items-center gap-2 text-xs text-blue-800" data-testid="approval-attachment-indicator">
              <FileText size={14} weight="fill" />
              <span>PDF πρακτικών — προσαρτάται αυτόματα</span>
              <CheckCircle size={14} weight="fill" className="ml-auto text-emerald-600" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="approval-cancel-btn">Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={!hasEmails || sending}
            className="bg-amber-600 hover:bg-amber-700 rounded-none disabled:opacity-50"
            data-testid="approval-send-btn"
            title={!hasEmails ? 'Δεν υπάρχουν emails' : ''}
          >
            {sending ? 'Αποστολή…' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
